import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AudioUtil from "../utils/audio";
import {
  nextSong,
  nextJoinedSong,
  pause,
  prevSong,
  play,
} from "../store/actions/playerActions";
import { getPlayer } from "../store/selectors/playerSelectors";
import Contols from "./Contols";
import Progress from "./Progress";
import VolumeControl from "./VolumeControl";
import { IPlayerState } from "../store/reducers/playerReducer";
import "./player.sass";
import { createPortal } from "react-dom";
import getCookie from "../utils/cookie-handler";

const audio = new AudioUtil();
let vol = getCookie("volume");
if (vol) audio.setVolume(vol);

function Player() {
  const initialTime = "0:00";
  const initialTrackTitle = "";
  const initialTrackChannel = 1;
  const trackTitleUpdateInterval = 10;
  var lastTrackTitleUpdateTime = 1;
  const playerState: IPlayerState = useSelector(getPlayer);
  const [progress, setProgress] = useState(0);
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [currentTrackTitle, setCurrentTrackTitle] = useState(initialTrackTitle);
  const [currentTrackChannel, setCurrentTrackChannel] =
    useState(initialTrackChannel);
  const [duration, setDuration] = useState(initialTime);
  const [volume, setVolume] = useState(audio.getVolume());
  const [channelID, setChannelID] = useState(window.CHANNEL_ID || 1);
  const loadHandler = () => {
    setDuration(audio.getDuration());
  };
  const pauseHandler = () => {
    dispatch(pause());
  };
  const playHander = () => {
    dispatch(play());
  };
  const endedHandler = () => {
    dispatch(nextJoinedSong());
  };

  let currentRequestController = null;
  async function parseSongTitle(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let title = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const startIndex = chunk.indexOf("StreamTitle=");

      if (startIndex !== -1) {
        const endIndex = chunk.indexOf(";", startIndex);
        title += chunk.substring(startIndex + 12, endIndex);
        if (currentRequestController) {
          currentRequestController.abort(); // Abort any existing request
        }
        title = title.replace(/^'(.*)'$/, "$1");
        return title;
      }
    }
  }
  function updateCurrentTrackTitleRequest() {
    var headers = new Headers();
    headers.append("Icy-MetaData", "1");
    var request = new Request(audio.audio.src, {
      method: "GET",
      headers: headers,
      mode: "cors", // Allow cross-origin requests
    });

    currentRequestController = new AbortController();
    const signal = currentRequestController.signal;
    fetch(request, { signal })
      .then((response) => {
        return parseSongTitle(response);
      })
      .then((songTitle) => {
        setCurrentTrackTitle(songTitle);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
        } else {
          console.error(
            "There has been a problem with your fetch operation:",
            error
          );
        }
      });
  }

  function updateCurrentTrackTitle() {
    setCurrentTrackChannel(channelID);
    updateCurrentTrackTitleRequest();
  }

  const timeUpdateHandler = () => {
    if (lastTrackTitleUpdateTime != 0 && audio.getTime() == "0:00") {
      lastTrackTitleUpdateTime = 0;
      updateCurrentTrackTitle();
    }
    if (
      audio.getTimeRaw() >
      lastTrackTitleUpdateTime + trackTitleUpdateInterval
    ) {
      lastTrackTitleUpdateTime = audio.getTimeRaw();
      updateCurrentTrackTitle();
    }
    setProgress(audio.getProgress());
    setCurrentTime(audio.getTime());
  };
  const progressHandler = (time: number) => {
    audio.setTime(time);
  };
  const volumeChangeHandler = (value) => {
    setVolume(value);
    audio.setVolume(value);
  };
  const nextHandler = () => {
    dispatch(nextSong());
  };
  const prevHandler = () => {
    dispatch(prevSong());
  };

  useEffect(() => {
    window.async.addEventListener("pageloaded", () => {
      setChannelID(window.CHANNEL_ID || 1);
    });
  }, []);

  useEffect(() => {
    audio.addEvent("loadedmetadata", loadHandler);
    audio.addEvent("ended", endedHandler);
    audio.addEvent("timeupdate", timeUpdateHandler);
    return () => {
      audio.removeEvent("loadedmetadata", loadHandler);
      audio.removeEvent("ended", endedHandler);
      audio.removeEvent("timeupdate", timeUpdateHandler);
    };
  }, []);

  useEffect(() => {
    if (!playerState.current) {
      return;
    }
    const { path, $element } = playerState.current;
    if (playerState.play) {
      audio.play(path, playerState.current);
      updateCurrentTrackTitle();
      let playingElement: HTMLElement | null = document.querySelector(
        ".verical-timeline-item.playing"
      );
      if (!playingElement)
        playingElement = document.querySelector(".program-item-btn.playing");
      if (playingElement) {
        playingElement.classList.remove("playing");
      }
      $element.addClass("playing");
    } else {
      audio.pause();
      setCurrentTrackTitle("");
      $element.removeClass("playing");
    }
  }, [playerState.play, playerState.current]);

  const { current } = playerState;

  let upthName = `upper-meta-holder-${currentTrackChannel}`;
  let upperTrackNameHolder = document.getElementById(upthName)
    ? createPortal(
        currentTrackTitle ? <span>{currentTrackTitle}</span> : null,
        document.getElementById(upthName)
      )
    : "";

  return (
    <div className={`player-main radio-${channelID} ${current && "visible"}`}>
      <div className="ml10 title-wrapper">
        <Progress
          time={currentTime}
          duration={duration}
          progress={progress}
          onProgress={progressHandler}
        />
        {current && (
          <div className="player-info">
            <a target="_blank" href={current.title_url}>
              <div className="player-info-title">{current.title}</div>
            </a>
            <div className="player-info-title">
              {currentTrackTitle}
              {upperTrackNameHolder}
            </div>
            <a target="_blank" style={{cursor:"pointer"}} href={(current.period_item_url.includes("play-live.html")) ? null : current.period_item_url} onClick={(current.period_item_url.includes("play-live.html")) ? playHander: null}>
              <div className="player-info-description">
                ({current.channel} {current.date} {current.time})
              </div>
            </a>
          </div>
        )}
      </div>
      <Contols
        list={playerState.list}
        playing={playerState.play}
        current={playerState.current}
        onPauseClick={pauseHandler}
        onPlayClick={playHander}
        onNextClick={nextHandler}
        onPrevClick={prevHandler}
      />
      <div className="volume-wrapper">
        <VolumeControl volume={volume} onVolumeChange={volumeChangeHandler} />
      </div>
    </div>
  );
}

export default Player;
